import React, { useEffect, useRef, useState } from "react"
import { Modal, Input } from "antd"
import type { TextAreaRef } from "antd/es/input/TextArea"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTerminal, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { JobStatusResponse } from "types/automation.types"
import { formatJobStatusForConsole } from "utils/automationUtils"

const { TextArea } = Input

interface JobConsoleModalProps {
  /** Whether the modal is visible */
  isVisible: boolean
  /** Job status data to display */
  jobStatusData: JobStatusResponse | null
  /** Whether the job status is currently being polled */
  isPolling: boolean
  /** Callback to close the modal */
  onClose: () => void
}

/**
 * Modal component to display job console output with real-time updates
 */
const JobConsoleModal: React.FC<JobConsoleModalProps> = ({
  isVisible,
  jobStatusData,
  isPolling,
  onClose,
}) => {
  const textAreaRef = useRef<TextAreaRef>(null)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const lastContentLengthRef = useRef(0)

  // Auto-scroll to bottom when content updates, unless user is manually scrolling
  useEffect(() => {
    if (!jobStatusData || !textAreaRef.current) return

    const currentContent = formatJobStatusForConsole(jobStatusData)
    const contentChanged = currentContent.length !== lastContentLengthRef.current
    lastContentLengthRef.current = currentContent.length

    if (contentChanged && !isUserScrolling) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const textarea = textAreaRef.current?.resizableTextArea?.textArea
        if (textarea) {
          textarea.scrollTop = textarea.scrollHeight
        }
      }, 0)
    }
  }, [jobStatusData, isUserScrolling])

  // Detect when user manually scrolls
  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget
    const isAtBottom =
      Math.abs(textarea.scrollHeight - textarea.scrollTop - textarea.clientHeight) < 10

    // If user scrolls to bottom, resume auto-scrolling
    // If user scrolls away from bottom, pause auto-scrolling
    setIsUserScrolling(!isAtBottom)
  }

  // Reset scroll state when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      setIsUserScrolling(false)
      lastContentLengthRef.current = 0
    }
  }, [isVisible])

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faTerminal} style={{ color: "var(--dq-brand)" }} />
          <span>Job Console - {jobStatusData?.script || "Loading..."}</span>
          {isPolling && (
            <FontAwesomeIcon
              icon={faSpinner}
              className="fa-spin text-sm"
              style={{ color: "var(--dq-brand)" }}
            />
          )}
        </div>
      }
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width="80%"
      style={{ top: 20 }}
    >
      {jobStatusData ? (
        <TextArea
          ref={textAreaRef}
          value={formatJobStatusForConsole(jobStatusData)}
          readOnly
          autoSize={{ minRows: 20, maxRows: 30 }}
          onScroll={handleScroll}
          className="font-mono text-xs bg-[#1e1e1e] text-[#d4d4d4] border border-[#404040] leading-relaxed [&:hover]:bg-[#1e1e1e] [&:focus]:bg-[#1e1e1e] [&:hover]:border-[#404040] [&:focus]:border-[#404040]"
        />
      ) : (
        <div className="text-center py-8 text-gray-500">
          Loading job status...
        </div>
      )}
    </Modal>
  )
}

export default JobConsoleModal
